import Box from "@mui/material/Box";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-community";
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fromGenericBeToGenericFe, onGridReady, useActionColDef, resolveFilterCodes} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {locationGroupAtom} from "../../state/locationGroupsRecoil";
import {productCodesAtom, searchedLocationGroupProductCodeAtom,} from "../../state/locationGroupProductCodesRecoil";
import {useProductCodeLocationGroupRepository} from "../../api/repositories/ProductCodeLocationGroupRepository";
import {AgGridReact} from "ag-grid-react";
import {useAgGridLocale} from "../../../../config/globalConfig";
import SearchFilters from "../../common/components/SearchFilters";
import {
    DialogInterface,
    FormDialog,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE
} from "../../api/types/generic";
import {Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Tooltip, useTheme} from "@mui/material";
import * as yup from "yup";
import {
    FormLocationGroupProductCodeModel,
    FormSearchLocationGroupProductCodeModel,
} from "../../api/types/locationGroupProductCode";
import Loading from "../../common/components/Loading";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {FormAutocomplete} from "../../common/form-fields";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import {useProductCodeRepository} from "../../api/repositories/ProductCodeRepository";
import BasicToolbar from "../../common/components/BasicToolbar";
import {Delete} from "@mui/icons-material";
import {useMutation} from "@tanstack/react-query";
import DialogComponent from "../../common/components/DialogComponent";
import {ProductCodeSelectFE} from "../../api/types/productCode";

const ProductCodeLocationGroup = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    // Use Recoil
    const [locationGroups, setLocationGroups] = useRecoilState(locationGroupAtom);
    const [productCodes, setProductCodes] = useRecoilState(productCodesAtom);
    const [searchedLocationGroupProductCode, setSearchedGroupAndCode] = useRecoilState(searchedLocationGroupProductCodeAtom);

    // Use Repository Hooks
    const {getProductCodes} = useProductCodeRepository();
    const {
        getLocationGroupProductCodesPaged,
        postLocationGroupProductCodeAll,
        getUnassociatedLocationGroupsByProductCode
    } = useProductCodeLocationGroupRepository();
    const {deleteLocationGroupProductCode} = useProductCodeLocationGroupRepository();

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchLocationGroupProductCodeModel>({
        locationGroup: "",
        productCode: "",
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const customSort = "ProductCodeLocationGroupEntityResponse";

    const loadProductCodes = async (legalEntities:string, dealers:string) => {
        try {
            const productCodes = await getProductCodes(legalEntities, dealers);
            setProductCodes((fromGenericBeToGenericFe(productCodes) as ProductCodeSelectFE[])
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((el) => ({...el, label: el.code as string + " - " + el.label}))
            );
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };


    const handleGetLocationGroupProductCodesPage = () => {
        gridRef.current?.api.refreshServerSide({purge: true});
    };

    const handleTriggerFilters = (entries: FormSearchLocationGroupProductCodeModel) => {
        const hardcodedFilter = Object.entries(entries).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: {type: "contains", filter: value}
        }), {});

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: dealer e legalEntity sono obbligatori per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con le liste vuote la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            locationGroup: "",
            productCode: "",
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
        handleTriggerFilters({
            locationGroup: "",
            productCode: "",
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            locationGroup: testFilters.locationGroup,
            productCode: testFilters.productCode,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            dealers: resolveFilterCodes(testFilters.dealers, dealerFilteredOptions),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleConfirmSaveLocationGroupProductCode = async (formValues: FormLocationGroupProductCodeModel) => {
        try {
            const productCodeLocationGroupsIds = formValues.locationGroup.map((locationGroup: SelectElementModelFE) => {
                return {
                    id: {
                        productCodeId: formValues.productCode?.code as string,
                        locationGroupId: locationGroup?.code as number
                    }
                };
            });
            await postLocationGroupProductCodeAll(productCodeLocationGroupsIds);
            handleGetLocationGroupProductCodesPage();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            setFormDialog(null);
            form.reset();
            setLocationGroups([]);
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            productCode: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            locationGroup: yup
                .array()
                .of(
                    yup.object().shape({
                        code: yup.string(),
                        label: yup.string(),
                    })
                )
                .min(1)
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const form = useForm<FormLocationGroupProductCodeModel>({
        defaultValues: {legalEntity: null, locationGroup: [], productCode: null},
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });
    const watchAllForm = form.watch();
    // ************ FORM - END ************ //

    // ************ AG GRID START ************ //
    const mutationDeleteProductCodeLocationGroup = useMutation({
        mutationFn: (data: any) => deleteLocationGroupProductCode({
            id: {locationGroupId: data.locationGroup.id, productCodeId: data.productCode.id}
        }),
        onSuccess: () => {
            handleGetLocationGroupProductCodesPage();
        }
    });

    const handleConfirmDelete = async (data: any) => {
        try {
            await mutationDeleteProductCodeLocationGroup.mutate(data);
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const deleteButtonClicked = (params: any) => {
        setDialog({
            open: true,
            message: t(
                "pricing:locations.dialogContents.removeLocationGroupProductCode"
            ),
            onClickConfirm: () => handleConfirmDelete(params.data),
        });
    };

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            headerName: t("pricing:locations.table.headers.code"),
            headerTooltip: t("pricing:locations.table.headers.code"),
            field: "productCode.id",
            colId: "productCode",
            flex: 1,
            filter: "text",
            pinned: "left"
        },
        {
            field: "legalEntities",
            headerName: t("pricing:peaks.table.headers.legalEntities"),
            headerTooltip: t("pricing:upsellingRate.table.headers.legalEntities"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.productCode.legalEntity)),
            pinned: "left",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:peaks.table.headers.dealers"),
            headerTooltip: t("pricing:upsellingRate.table.headers.dealers"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.productCode.dealer)),
            pinned: "left",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            headerName: t("pricing:locations.table.headers.name"),
            headerTooltip: t("pricing:locations.table.headers.name"),
            field: "productCode.name",
            colId: "productCode",
            flex: 1,
            sortable: false,
            pinned: "left"
        },
        {
            headerName: t("pricing:locations.table.headers.description"),
            headerTooltip: t("pricing:locations.table.headers.description"),
            field: "productCode.description",
            colId: "productCode",
            flex: 1,
            sortable: false,
            pinned: "left"
        },
        {
            headerName: t("pricing:locations.table.headers.commercialSegment"),
            headerTooltip: t("pricing:locations.table.headers.commercialSegment"),
            field: "productCode.commercialCategory.commercialSegment.name",
            colId: "commercialSegment",
            flex: 1,
            sortable: false,
            pinned: "left"
        },
        {
            headerName: t("pricing:locations.table.headers.commercialCategory"),
            headerTooltip: t("pricing:locations.table.headers.commercialCategory"),
            field: "productCode.commercialCategory.name",
            colId: "commercialSegment",
            flex: 1,
            sortable: false,
            pinned: "left"
        },
        {
            headerName: t("pricing:locations.table.headers.locationGroup"),
            headerTooltip: t("pricing:locations.table.headers.locationGroup"),
            field: "locationGroup.name",
            colId: "locationGroup",
            flex: 1,
            filter: "text"
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_DELETE_PRODUCT_CODE_LOCATION_GROUP";
                    });
                }) &&
                <Tooltip arrow title={t("pricing:tooltips.delete")} placement={"right-start"}>
                    <IconButton
                        aria-label="close"
                        onClick={() => deleteButtonClicked(params)}
                        sx={{
                            margin: "auto",
                            color: theme.palette.accent.main
                        }}
                    >
                        <Delete/>
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchLocationGroupProductCodeModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);

                function getCustomSort() {
                    let currentSort = sort;
                    switch (customSort) {
                        case "ProductCodeLocationGroupEntityResponse": {
                            switch (sort) {
                                case "locationGroup": {
                                    currentSort = "locationGroup_name";
                                    break
                                }
                                case "productCode": {
                                    currentSort = "productCode_id";
                                    break
                                }
                                case "legalEntities": {
                                    currentSort = "productCode_legalEntity";
                                    break
                                }
                                case "dealers": {
                                    currentSort = "productCode_dealer";
                                    break
                                }
                            }
                            break;
                        }
                    }

                    return currentSort;
                }

                const locationGroupProductCodesPaged = await getLocationGroupProductCodesPaged({
                    ...filter,
                    sort: sort && order ? `${customSort ? getCustomSort() : sort},${order}` : "",
                    size: gridRef?.current?.api?.paginationGetPageSize() || 10,
                    page: gridRef?.current?.api?.paginationGetCurrentPage() || 0
                });
                if (locationGroupProductCodesPaged.totalElements === 0) {
                    gridRef.current!.api.showNoRowsOverlay();
                } else {
                    gridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: locationGroupProductCodesPaged.content,
                    rowCount: locationGroupProductCodesPaged.totalElements,
                });
            },
        };
    };
    // ************ AG GRID END ************ //

    const filters: GenericComponentModel[] = [
        {
            name: "productCode",
            value: testFilters.productCode as string,
            label: `${t("pricing:locations.form.labels.code")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                productCode: value
            }),
            options: [],
            type: "TextField"
        },
        {
            name: "locationGroup",
            value: testFilters.locationGroup as string,
            label: `${t("pricing:locations.form.labels.locationGroup")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                locationGroup: value
            }),
            options: [],
            type: "TextField"
        },
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOption || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    const handleChangeLocationGroup = async (value: SelectElementModelFE[]) => {
        const currentValue: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            value.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.locationGroup?.find(
                (el: SelectElementModelFE) => el.code === 0
            )
                ? // applica il "selectAll
                locationGroups.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                value.filter((el: SelectElementModelFE) => el.code !== 0);

        form.setValue("locationGroup", currentValue);
    };

    const formLocationGroupProductCode = (
        <FormProvider {...form}>
            <Grid container spacing={2} alignItems="start">
                <Grid item xs={12} md={6}>
                    <FormAutocomplete
                        {...form.register("legalEntity")}
                        label={`${t("pricing:locations.form.labels.legalEntity")}`}
                        options={legalEntityOptions}
                        handleChange={(value: SelectElementModelFE) => {
                            form.setValue("legalEntity", value);
                            loadProductCodes(value.code as string, dealerFilteredOptions.join(","));
                        }
                        }
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormAutocomplete
                        {...form.register("productCode")}
                        label={`${t("pricing:locations.form.labels.code")}*`}
                        options={productCodes}
                        multiple={false}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={async (value: SelectElementModelFE) => {
                            form.setValue("productCode", value);
                            form.setValue("locationGroup", []);
                            const locationGroups =
                                await getUnassociatedLocationGroupsByProductCode(
                                    value?.code as number,
                                    form.getValues("legalEntity")?.code as string
                                );
                            setLocationGroups([
                                {
                                    code: 0,
                                    label: t("pricing:actions.selectAll"),
                                },
                                ...fromGenericBeToGenericFe(locationGroups).sort((a, b) =>
                                    a.label.localeCompare(b.label)
                                ),
                            ]);
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...form.register("locationGroup")}
                        label={`${t("pricing:locations.form.labels.locationGroup")}*`}
                        options={locationGroups}
                        multiple={true}
                        handleChange={(value: SelectElementModelFE[]) =>
                            handleChangeLocationGroup(value)
                        }
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                    />
                </Grid>
            </Grid>
        </FormProvider>
    );

    const toolbar = {
        renderActions: () => (
            <BasicToolbar addIcon={
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_CREATE_PRODUCT_CODE_LOCATION_GROUP";
                    });
                }) ?
                    {
                        onClick: () => setFormDialog({
                            open: true,
                            onClickConfirm: form.handleSubmit(handleConfirmSaveLocationGroupProductCode),
                            readonly: false
                        })
                    } : undefined}/>
        )
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_PRODUCT_CODE_LOCATION_GROUP.name")}/>
            {formDialog?.open &&
                <Dialog open={formDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        form.reset();
                        setFormDialog(null);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:locations.section.titles.addGroupToCode")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        {formLocationGroupProductCode}
                    </DialogContent>
                    <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                        <Button variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    form.reset();
                                    setFormDialog(null);
                                }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={formDialog?.onClickConfirm}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            }
            {/* <CardContainer> */}
            <SearchFilters
                filtersOutOfCollapse={filters}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />
            {/* </CardContainer> */}
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={() => onGridReady({
                            initialSortColId: "productCode",
                            gridRef,
                            createDatasource
                        })}
                    />
                </Grid>
            </Grid>
            <Loading/>

            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />}
        </Box>
    );
};

export default ProductCodeLocationGroup;
